import { supabase } from '../lib/supabase';

export type TipoZeladoria =
  | 'buraco_pavimento'
  | 'iluminacao_publica'
  | 'coleta_lixo'
  | 'limpeza_urbana'
  | 'arvore_caida'
  | 'semaforo'
  | 'vazamento_agua'
  | 'pichacao'
  | 'terreno_baldio'
  | 'ponto_onibus'
  | 'praca_jardim'
  | 'transito'
  | 'outros';

export const TIPO_ZELADORIA_LABEL: Record<TipoZeladoria, string> = {
  buraco_pavimento: 'Buraco em via pública',
  iluminacao_publica: 'Iluminação pública',
  coleta_lixo: 'Coleta de lixo',
  limpeza_urbana: 'Limpeza urbana',
  arvore_caida: 'Árvore caída ou poda',
  semaforo: 'Semáforo / sinalização',
  vazamento_agua: 'Vazamento de água ou esgoto',
  pichacao: 'Pichação',
  terreno_baldio: 'Terreno baldio / mato alto',
  ponto_onibus: 'Ponto de ônibus',
  praca_jardim: 'Praça / jardim',
  transito: 'Trânsito / placa',
  outros: 'Outros',
};

export const TIPOS_ZELADORIA_LISTA: TipoZeladoria[] = [
  'buraco_pavimento', 'iluminacao_publica', 'coleta_lixo', 'limpeza_urbana',
  'arvore_caida', 'semaforo', 'vazamento_agua', 'pichacao', 'terreno_baldio',
  'ponto_onibus', 'praca_jardim', 'transito', 'outros',
];

export const TIPO_ZELADORIA_COR: Record<TipoZeladoria, string> = {
  buraco_pavimento: '#dc2626',
  iluminacao_publica: '#f59e0b',
  coleta_lixo: '#84cc16',
  limpeza_urbana: '#10b981',
  arvore_caida: '#16a34a',
  semaforo: '#ef4444',
  vazamento_agua: '#0ea5e9',
  pichacao: '#a855f7',
  terreno_baldio: '#737373',
  ponto_onibus: '#2563eb',
  praca_jardim: '#22c55e',
  transito: '#facc15',
  outros: '#64748b',
};

export interface AssuntoZeladoria {
  id: string;
  nome: string;
  descricao: string | null;
  prazo_dias: number | null;
}

export interface PontoMapaZeladoria {
  id: string;
  numero: string;
  titulo: string;
  tipo_zeladoria: string | null;
  status: string;
  endereco: string | null;
  bairro: string | null;
  lat: number;
  lng: number;
  data_abertura: string;
  data_conclusao: string | null;
}

export interface IndicadoresZeladoria {
  ano: number;
  total: number;
  resolvidas: number;
  abertas: number;
  em_atendimento: number;
  taxa_resolucao_percent: number;
  tempo_medio_horas: number;
  distribuicao_por_tipo: Record<string, number>;
  top_bairros: Array<{ bairro: string; qtd: number }>;
}

export interface AbrirAnonimaInput {
  estabelecimento_id: string;
  assunto_id: string;
  tipo_zeladoria: string;
  titulo: string;
  descricao: string;
  endereco?: string;
  cep?: string;
  bairro?: string;
  latitude?: number;
  longitude?: number;
  referencia?: string;
  contato_telefone?: string;
}

export interface AbrirIdentificadaInput extends AbrirAnonimaInput {
  cidadao_id: string;
  sigilosa?: boolean;
}

export const zeladoriaPortalService = {
  async listarAssuntos(estabelecimentoId: string): Promise<AssuntoZeladoria[]> {
    const { data, error } = await supabase.rpc('zeladoria_assuntos_publicos', {
      p_estabelecimento_id: estabelecimentoId,
    });
    if (error) throw error;
    return (data ?? []) as AssuntoZeladoria[];
  },

  async abrirAnonima(input: AbrirAnonimaInput): Promise<{ numero: string; hash_publico: string }> {
    const { data, error } = await supabase.rpc('zeladoria_abrir_anonima', {
      p_estabelecimento_id: input.estabelecimento_id,
      p_assunto_id: input.assunto_id,
      p_tipo_zeladoria: input.tipo_zeladoria,
      p_titulo: input.titulo,
      p_descricao: input.descricao,
      p_endereco: input.endereco ?? null,
      p_cep: input.cep ?? null,
      p_bairro: input.bairro ?? null,
      p_latitude: input.latitude ?? null,
      p_longitude: input.longitude ?? null,
      p_referencia: input.referencia ?? null,
      p_contato_telefone: input.contato_telefone ?? null,
    });
    if (error) throw error;
    const r = data as { ok?: boolean; erro?: string; numero?: string; hash_publico?: string };
    if (r.erro) throw new Error(traduzirErro(r.erro));
    return { numero: r.numero!, hash_publico: r.hash_publico! };
  },

  // Para cidadao logado: insert direto na tabela documentos (RLS permite)
  async abrirIdentificada(input: AbrirIdentificadaInput): Promise<{ id: string; numero: string; hash_publico: string }> {
    const { data, error } = await supabase
      .from('documentos')
      .insert({
        estabelecimento_id: input.estabelecimento_id,
        modulo_codigo: 'zeladoria',
        assunto_id: input.assunto_id,
        titulo: input.titulo,
        descricao: input.descricao,
        status: 'aberto',
        prioridade: 'media',
        visibilidade: input.sigilosa ? 'restrita_lista' : 'publica',
        modo_identificacao: input.sigilosa ? 'sigilosa' : 'identificada',
        solicitante_cidadao_id: input.cidadao_id,
        endereco: input.endereco ?? null,
        cep: input.cep ?? null,
        bairro: input.bairro ?? null,
        latitude: input.latitude ?? null,
        longitude: input.longitude ?? null,
        campos_customizados: {
          tipo_zeladoria: input.tipo_zeladoria,
          referencia_local: input.referencia ?? '',
        },
      })
      .select('id, numero, hash_publico')
      .single();
    if (error) throw error;
    return data as { id: string; numero: string; hash_publico: string };
  },

  async mapa(estabelecimentoId: string, tipo?: string, apenasAbertas?: boolean): Promise<PontoMapaZeladoria[]> {
    const { data, error } = await supabase.rpc('zeladoria_mapa_publico', {
      p_estabelecimento_id: estabelecimentoId,
      p_tipo_zeladoria: tipo ?? null,
      p_apenas_abertas: apenasAbertas ?? false,
    });
    if (error) throw error;
    return (data ?? []) as PontoMapaZeladoria[];
  },

  async indicadores(estabelecimentoId: string, ano?: number): Promise<IndicadoresZeladoria> {
    const { data, error } = await supabase.rpc('zeladoria_indicadores_publicos', {
      p_estabelecimento_id: estabelecimentoId,
      p_ano: ano ?? null,
    });
    if (error) throw error;
    return data as IndicadoresZeladoria;
  },
};

const ERROS: Record<string, string> = {
  titulo_curto: 'Informe um título com pelo menos 3 caracteres.',
  descricao_curta: 'Detalhe a demanda (mínimo 10 caracteres).',
  tipo_obrigatorio: 'Selecione o tipo de problema.',
  assunto_invalido: 'Categoria inválida ou desativada.',
  assunto_nao_e_zeladoria: 'Categoria não pertence ao módulo Zeladoria.',
};

function traduzirErro(c: string): string {
  return ERROS[c] ?? `Erro: ${c}`;
}
